import * as React from "react";
import { QRCodeSVG } from "qrcode.react";
import dayjs from "dayjs";

import Logo from "assets/images/logo-black.png";

import { ReceiptReturnResponse } from "api/models";

import { getPriceFormat } from "shared/functions";

import "./index.scss";

interface Props {
  receiptReturn: ReceiptReturnResponse | null;
}

const ReceiptReturnComponent: React.FC<Props> = (props) => {
  const { receiptReturn } = props;

  if (receiptReturn) {
    const {
      companyName,
      companyAddress,
      localNumber,
      receiptProducts = 0,
      totalAmount = 0,
      productsTotalAmount = 0,
      roundingAmount = 0,
      discountAmount = 0,
      discountPercentage = 0,
      receiptTimeUtc,
      pointOfSaleName,
      qrReceiptUrl,
      isFiscalReceipt,
      companyTin,
      cashierName,
      fiscalNumber,
      cashRegisterFiscalNumber,
      reportName,
      isOffline,
      isCashPayment,
      receiptFiscalNumber,
      receiptCardPayment,
      offlineCheckSum,
      nonCashPaymentType,
    } = receiptReturn;

    const {
      acquirerIdentifier,
      terminalIdentifier,
      maskedPan,
      paymentSystem,
      approvalCode,
      rrn,
    } = receiptCardPayment || {};

    const receiptItem = (title: string, data: string | null | number = "", price?: boolean) => {
      return (
        <div className="receiptSummary__text-wrapper">
          <div>
            <p className="receiptSummary__item">{title}</p>
          </div>
          <div className={`receiptSummary__value-wrapper ${price && "receiptSummary__price-wrapper"}`}>
            <p className={`receiptSummary__item ${price && "receiptSummary__price"}`}>
              {data}
            </p>
          </div>
        </div>
      );
    };

    return (
      <div>
        <section className="receiptReturn">
          <div className="receiptReturn__wrapper">
            <p className="receiptReturn__text">
              {pointOfSaleName}
              <br />
              {companyName}
            </p>
            <p className="receiptReturn__text">{companyAddress}</p>
            {isFiscalReceipt && (
              <p className="receiptReturn__text">
                ІД: {companyTin}<br />
                Касир: {cashierName}
              </p>
            )}
          </div>
          <div className="receiptReturn__wrapper">
            <div className="receiptReturn__text-wrapper">
              <div>
                <p className="receiptReturn__text">
                  Повернення для документа № {receiptFiscalNumber}
                </p>
              </div>
            </div>
          </div>

          <div className="receiptReturn__wrapper">
            {/*eslint-disable-next-line @typescript-eslint/ban-ts-comment*/}
            {/*@ts-ignore*/}
            {receiptProducts &&
              receiptProducts.map(
                ({
                  name,
                  price = 0,
                  quantity = 0,
                  productProperties,
                  barcode,
                  unit,
                }) => (
                  <div key={name} className="receiptReturn__text-wrapper">
                    <p className="receiptReturn__text receiptReturn__item">
                      {unit.isDiscrete
                        ? Number(quantity).toFixed(2)
                        : Math.round(quantity)}{" "}
                      {unit.symbol}. x {getPriceFormat(price)} =
                      <br />
                      {!!barcode && barcode}
                      {!!barcode && <br />}
                      {!!productProperties && productProperties}
                      {!!productProperties && <br />}
                      {name}
                    </p>
                    <p className="receiptReturn__text">
                      <span className="receiptReturn__price">
                        {getPriceFormat(price * quantity)}
                      </span>
                    </p>
                  </div>
                )
              )}
          </div>
          {receiptCardPayment && (
            <div className="receiptReturn__wrapper">
              {receiptItem("ІДЕНТ. ЕКВАЙРА  ", acquirerIdentifier)}
              {receiptItem("ТЕРМІНАЛ", terminalIdentifier)}
              {receiptItem("ЕПЗ", maskedPan)}
              {receiptItem("ПЛАТІЖНА СИСТЕМА ", paymentSystem)}
              {receiptItem("КОД АВТ.", approvalCode)}
              {receiptItem("RRN", rrn)}
            </div>
          )}
          <div className="receiptReturn__wrapper">
            {isCashPayment && receiptItem("Форма оплати:", "Готівка, ГРН")}
            {!isCashPayment && (
              <>
                {receiptItem("Форма оплати:", "Безготівкова, ГРН")}
                {receiptItem("Вид операції:", "Оплата")}
                {receiptItem("Засіб оплати:", nonCashPaymentType?.name || "Електронний платіжний засіб")}
              </>
            )}
            {receiptItem("Сума:", getPriceFormat(productsTotalAmount), true)}
            {!!discountAmount &&
              receiptItem(
                `Знижка${discountPercentage ? ` ${discountPercentage}%` : ""}, грн`,
                getPriceFormat(discountAmount),
                true
              )}
            {isCashPayment && !!roundingAmount && receiptItem("Заокруглення:", getPriceFormat(roundingAmount), true)}
            <div className="receiptReturn__text-wrapper">
              <p className="receiptReturn-text receiptReturn__text receiptSummary__top">
                ДО СПЛАТИ:
              </p>
              <p className="receiptReturn-text receiptReturn__text receiptSummary__top">
                {getPriceFormat(totalAmount)}
              </p>
            </div>
          </div>
          <div className="receiptReturn__wrapper">
            <div className="receiptReturn__text-wrapper">
              <div>
                <p className="receiptReturn__text">Без ПДВ</p>
              </div>
            </div>
          </div>
          <div className="receiptReturn__wrapper">
            <div className="receiptReturn__text-wrapper">
              {fiscalNumber && <p className="receiptReturn__text">ЧЕК № {fiscalNumber}</p>}
              <p className="receiptReturn__text">ВН: {localNumber}</p>
            </div>
            <div className="receiptReturn__text-wrapper">
              <p className="receiptReturn__text">
                Дата:{" "}
                {dayjs
                  .utc(receiptTimeUtc)
                  .local()
                  .format("DD.MM.YYYY, HH:mm:ss")}
              </p>
            </div>
          </div>

          <div className="receiptReturn__wrapper">
            <div className="receiptReturn__qrcode-wrapper">
              <QRCodeSVG value={qrReceiptUrl || ""} size={160} level="Q" />
            </div>
            {isFiscalReceipt && (
              <div className="receiptReturn__text-wrapper">
                <div>
                  <p className="receiptReturn__text">
                    Режим роботи: {isOffline ? "офлайн" : "онлайн"}
                  </p>
                  {isOffline && (
                    <p className="receiptReturn__text">
                      Контрольне число: {offlineCheckSum}
                    </p>
                  )}
                  {isFiscalReceipt && (
                    <p className="receiptReturn__text">
                      ФН ПРРО: {cashRegisterFiscalNumber}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          <p className="receiptReturn__text">{reportName}</p>

          <div className="receiptReturn__img-wrapper">
            <img width={70} src={Logo} />
          </div>
        </section>
      </div>
    );
  }

  return null;
};

export default ReceiptReturnComponent;
